import { Project } from '../../types/project';
import { defaultEditionForState, EditionPack, getEditionPack } from './editions';

// One resolver for every section number the MEASURED path prints. The
// traditional engines already carry their citations in the result object
// (`sectionCitation`); the measured path had none, because it predates the
// edition packs and hardcoded "220.87" in ~40 strings across the report, the
// results screen, the data-quality notes and the completion blockers.
//
// That was wrong wherever the 2026 NEC is enforced — Massachusetts since
// 2026-04-24 — because the 2026 edition moved every load calculation rule out
// of Article 220 into a new Article 120. A report handed to a Massachusetts
// inspector citing 220.87 reads as written against the wrong code.
//
// Same discipline as editions.ts: no consumer writes a literal, and a null map
// entry renders the sentence without a citation rather than with an inferred
// one.

export interface ProjectCitations {
  pack: EditionPack;
  /** '2017' | '2020' | '2023' | '2026' — the edition these citations resolve under. */
  edition: string;
  /** The measured-demand section: '220.87', or '120.87' under the 2026 pack. */
  measured: string;
  /** 'Article 220' or 'Article 120'. */
  article: string;
  /** The noncoincident-load section: '220.60' or '120.6'. */
  noncoincident: string;
  /** Conductor and OCPD sizing sections, or null where unverified for the
   * edition — callers must drop the citation, not substitute a guess. */
  conductorSizing: string | null;
  /** Provenance for a provisional pack, for the report's disclosure block.
   * Null when the pack is fully verified against the primary text. */
  editionNote: string | null;
}

/**
 * Resolve every measured-path citation for a project.
 *
 * The edition comes from the project's own `code_edition` when set, and
 * otherwise from the customer's state — which is what makes a Massachusetts
 * project cite Article 120 without anybody selecting anything.
 */
export function citationsFor(project: Partial<Project>): ProjectCitations {
  const pack = getEditionPack(
    project.code_edition ?? defaultEditionForState(project.customer_state)
  );
  const map = pack.sectionMap;
  return {
    pack,
    edition: pack.edition,
    // Non-null in every shipped pack; the fallback keeps a future pack that
    // omits one from printing "null" into a permit document.
    measured: map.determiningExisting ?? '220.87',
    article: map.article ?? 'Article 220',
    noncoincident: map.noncoincident ?? '220.60',
    conductorSizing: map.conductorSizing,
    editionNote: pack.provisional
      ? `Edition pack note (NEC ${pack.edition}): ${pack.verification}`
      : null,
  };
}

/** The measured method's stored methodology version, edition-correct. */
export function measuredMethodologyVersion(project: Partial<Project>): string {
  return `NEC ${citationsFor(project).measured} v1.0`;
}

/** The traditional method's stored methodology version, edition-correct:
 * 'NEC 220.83 v1.0' under the 2020/2023 packs, 'NEC 120.83 v1.0' under 2026.
 * Callers used to write the 2023 literal, which then printed an Article 220
 * number on an Article 120 report. */
export function traditionalMethodologyVersion(project: Partial<Project>): string {
  const pack = getEditionPack(
    project.code_edition ?? defaultEditionForState(project.customer_state)
  );
  return `NEC ${pack.sectionMap.existingDwelling ?? '220.83'} v1.0`;
}
